import { typography as designTypography, spacing } from "../../../app/design-system";
import { Form,Input,Radio,Space,Typography } from "../../../../../../../shared/frontend/ui";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { ThemedModal } from "../../../components/ThemedModal";
import type { PersonDocumentRow } from "./personDetailTypes";

type Props = {
  text: I18nContextValue["text"];
  uploadOpen: boolean;
  uploadFile: File | null;
  uploadTitle: string;
  uploadDescription: string;
  uploading: boolean;
  onUploadTitleChange: (value: string) => void;
  onUploadDescriptionChange: (value: string) => void;
  onUploadCancel: () => void;
  onUploadSubmit: () => void;
  resendOpen: boolean;
  resendChannel: "email" | "brief24";
  resending: boolean;
  viewerDoc: PersonDocumentRow | null;
  onResendChannelChange: (value: "email" | "brief24") => void;
  onResendCancel: () => void;
  onResendSubmit: () => void | Promise<void>;
};

export function PersonDocumentDialogs({
  text,
  uploadOpen,
  uploadFile,
  uploadTitle,
  uploadDescription,
  uploading,
  onUploadTitleChange,
  onUploadDescriptionChange,
  onUploadCancel,
  onUploadSubmit,
  resendOpen,
  resendChannel,
  resending,
  viewerDoc,
  onResendChannelChange,
  onResendCancel,
  onResendSubmit
}: Props) {
  return (
    <>
      <ThemedModal
        title={text("Upload document")}
        open={uploadOpen}
        formId="person-detail-upload-document-form"
        onCancel={onUploadCancel}
        okText={text("Upload")}
        cancelText={text("Cancel")}
        submitting={uploading}
      >
        <Form id="person-detail-upload-document-form" layout="vertical" onFinish={onUploadSubmit}>
          <Form.Item label={text("File")}>
            <Input value={uploadFile?.name || ""} disabled />
          </Form.Item>
          <Form.Item label={text("Title")} extra={text("e.g. Cancellation confirmation, Contract, etc.")}>
            <Input
              value={uploadTitle}
              onChange={(event) => onUploadTitleChange(event.target.value)}
              placeholder={text("Enter title...")}
              maxLength={500}
            />
          </Form.Item>
          <Form.Item label={text("Description")} extra={text("Optional note or description for the document")}>
            <Input.TextArea
              value={uploadDescription}
              onChange={(event) => onUploadDescriptionChange(event.target.value)}
              placeholder={text("Enter description...")}
              maxLength={2000}
              rows={3}
            />
          </Form.Item>
        </Form>
      </ThemedModal>

      <ThemedModal
        title={text("Resend document")}
        open={resendOpen}
        onCancel={onResendCancel}
        okText={text("Resend")}
        cancelText={text("Cancel")}
        autoFocusFirstField={false}
        onOk={() => onResendSubmit()}
        confirmLoading={resending}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: spacing.lg }}>
          {text("How should the document be sent?")}
        </Typography.Paragraph>
        <Radio.Group
          value={resendChannel}
          onChange={(event) => onResendChannelChange(event.target.value)}
          disabled={!viewerDoc}
          style={{ display: "flex", flexDirection: "column", gap: spacing.md }}
        >
          <Radio value="email">
            <Space orientation="vertical" size={0}>
              <strong>{text("Email")}</strong>
              <Typography.Text type="secondary" style={{ fontSize: designTypography.fontSize.sm }}>
                {text("Document sent to person email")}
              </Typography.Text>
            </Space>
          </Radio>
          <Radio value="brief24" disabled>
            <Space orientation="vertical" size={0}>
              <strong>{text("Online letter (Brief24)")}</strong>
              <Typography.Text type="secondary" style={{ fontSize: designTypography.fontSize.sm }}>
                {text("Coming soon.")}
              </Typography.Text>
            </Space>
          </Radio>
        </Radio.Group>
      </ThemedModal>
    </>
  );
}
